// What this browser runs on, for recommending how to set up (Getting ready, the dashboard): the Mac app on a Mac,
// the Windows app on Windows, cmx in a terminal on Linux (and on Windows, in WSL), nothing on a phone.

export type Device = 'mac' | 'windows' | 'linux' | 'mobile';

/** Where setup runs: the Mac app, the Windows app, or cmx in a terminal. */
export type Way = 'mac' | 'win' | 'cli';

/** From the user agent; an iPad asking for the desktop site is a tablet. */
export function deviceOf(ua: string, touchPoints: number): Device {
  if (/Android|iPhone|iPad|iPod|Mobile/.test(ua)) return 'mobile';
  if (/Macintosh|Mac OS X/.test(ua)) return touchPoints > 1 ? 'mobile' : 'mac';
  if (/Windows/.test(ua)) return 'windows';
  return 'linux'; // Linux, ChromeOS and the rest run cmx
}

export const thisDevice = (): Device => deviceOf(navigator.userAgent, navigator.maxTouchPoints);

/** An Android phone or tablet, which can install Cloud Machine for Android; an iPhone or iPad can't. */
export const isAndroid = (ua: string) => /Android/.test(ua);
export const onAndroid = () => isAndroid(navigator.userAgent);

/**
 * The ways to offer on this device, the recommended one first (spec "Getting ready", its table): an app once it can
 * be downloaded, else the terminal. A Mac: the Mac app, then the terminal. Windows: the Windows app, then the
 * terminal in WSL (the Mac app "For Mac" until there's a Windows app). Linux: the terminal, then the Mac app. A phone
 * or tablet takes none (setup runs on a computer): every way, to read.
 */
export function setupWays(d: Device, apps: { mac: boolean; windows: boolean }): { ways: Way[]; recommended: Way | null } {
  const mac: Way[] = apps.mac ? ['mac'] : [];
  switch (d) {
    case 'mobile':
      return { ways: [...mac, ...(apps.windows ? (['win'] as Way[]) : []), 'cli'], recommended: null };
    case 'mac':
      return { ways: [...mac, 'cli'], recommended: mac[0] ?? 'cli' };
    case 'windows':
      return apps.windows ? { ways: ['win', 'cli'], recommended: 'win' } : { ways: ['cli', ...mac], recommended: 'cli' };
    default:
      return { ways: ['cli', ...mac], recommended: 'cli' };
  }
}
